import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLinkIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InvoicePaymentsTable } from "@/components/InvoicePaymentsTable";
import { InvoiceRemindersTable } from "@/components/InvoiceRemindersTable";
import { InvoiceCreditNotesTable } from "@/components/InvoiceCreditNotesTable";
import { InvoiceAuditLogTable } from "@/components/InvoiceAuditLogTable";
import { formatCurrency, formatDateTime, formatNumber } from "@/lib/format";

type Invoice = components["schemas"]["InvoiceResponse"];
type LineItem = components["schemas"]["InvoiceLineItemResponse"];

function statusVariant(status: string | undefined): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Paid":
      return "default";
    case "Issued":
    case "Open":
      return "secondary";
    case "Credited":
      return "default";
    case "Void":
      return "destructive";
    default:
      return "outline";
  }
}

// An invoice is overdue only while it's unsettled (Sent / PartiallyPaid) and past its due date.
function overdueDays(invoice: Invoice): number {
  if (!invoice.dueDate) return 0;
  if (invoice.status !== "Issued" && invoice.status !== "PartiallyPaid") return 0;
  const diff = Date.now() - new Date(invoice.dueDate).getTime();
  return diff > 0 ? Math.floor(diff / 86_400_000) : 0;
}

function formatAddress(c: NonNullable<Invoice["customer"]>): string | null {
  const parts = [c.addressLine1, c.addressLine2, c.city, c.state, c.postalCode, c.country].filter(
    (p): p is string => Boolean(p),
  );
  return parts.length ? parts.join(", ") : null;
}

export function InvoicePage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!invoiceId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function fetchInvoice() {
      setLoading(true);
      const { data, error: apiError } = await api.GET("/admin/invoices/{id}", {
        params: { path: { id: invoiceId! } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load invoice.");
      } else {
        setInvoice(data ?? null);
      }
      setLoading(false);
    }

    fetchInvoice();
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  const organisationId = invoice?.organisationId ?? undefined;

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Invoice</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : invoice ? (
        <>
          <Card className="mb-8">
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <CardTitle>
                {invoice.documentNumber || <span className="text-muted-foreground">Unnamed</span>}
              </CardTitle>
              {invoice.paymentUrl && (
                <Button asChild variant="outline" size="sm">
                  <a href={invoice.paymentUrl} target="_blank" rel="noreferrer noopener">
                    Payment link
                    <ExternalLinkIcon className="ml-1 size-3" />
                  </a>
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <Section title="Invoice">
                <Field label="Invoice ID">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {invoice.id}
                  </Badge>
                </Field>
                <Field label="Organisation">
                  {invoice.organisationId ? (
                    <Link to={`/organisations/${invoice.organisationId}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {invoice.organisationId}
                      </Badge>
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </Field>
                <Field label="Status">
                  <div className="flex items-center gap-2">
                    <Badge variant={statusVariant(invoice.status)}>{invoice.status}</Badge>
                    {overdueDays(invoice) > 0 && (
                      <Badge variant="destructive">{overdueDays(invoice)}d overdue</Badge>
                    )}
                  </div>
                </Field>
                <Field label="Currency">{invoice.currency}</Field>
                <Field label="Issued">{formatDateTime(invoice.issueDate)}</Field>
                <Field label="Due">{formatDateTime(invoice.dueDate)}</Field>
                {invoice.recurringScheduleId && (
                  <Field label="Recurring schedule">
                    <Link to={`/recurring-invoices/${invoice.recurringScheduleId}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {invoice.recurringScheduleId.slice(0, 8)}...
                        {invoice.recurringOccurrenceNumber != null
                          ? ` · #${invoice.recurringOccurrenceNumber}`
                          : ""}
                      </Badge>
                    </Link>
                  </Field>
                )}
              </Section>

              <Section title="Customer">
                <Field label="Name">
                  {invoice.customerId && organisationId ? (
                    <Link
                      to={`/customers/${invoice.customerId}?organisationId=${organisationId}`}
                      className="hover:underline"
                    >
                      {invoice.customer?.name ?? "—"}
                    </Link>
                  ) : (
                    invoice.customer?.name ?? "—"
                  )}
                </Field>
                <Field label="Customer ID">
                  {invoice.customerId ? (
                    organisationId ? (
                      <Link
                        to={`/customers/${invoice.customerId}?organisationId=${organisationId}`}
                      >
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {invoice.customerId}
                        </Badge>
                      </Link>
                    ) : (
                      <Badge variant="secondary" className="font-mono text-xs">
                        {invoice.customerId}
                      </Badge>
                    )
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </Field>
                <Field label="Email">{invoice.customer?.email ?? "—"}</Field>
                <Field label="Phone">{invoice.customer?.phone ?? "—"}</Field>
                <Field label="Tax number">{invoice.customer?.taxNumber ?? "—"}</Field>
                <Field label="Address" className="sm:col-span-3">
                  {invoice.customer ? (formatAddress(invoice.customer) ?? "—") : "—"}
                </Field>
              </Section>

              <Section title="Amounts">
                <Field label="Subtotal">
                  {formatCurrency(invoice.subTotal, invoice.currency)}
                </Field>
                <Field label="Tax">
                  {formatCurrency(invoice.taxAmount, invoice.currency)}
                </Field>
                <Field label="Total">
                  <span className="font-semibold">
                    {formatCurrency(invoice.totalAmount, invoice.currency)}
                  </span>
                </Field>
                <Field label="Paid">
                  {formatCurrency(invoice.amountPaid, invoice.currency)}
                </Field>
                {Number(invoice.amountCredited ?? 0) > 0 && (
                  <Field label="Credit applied">
                    <span className="text-emerald-600 dark:text-emerald-400">
                      −{formatCurrency(invoice.amountCredited, invoice.currency)}
                    </span>
                  </Field>
                )}
                <Field label="Outstanding">
                  <span className="font-semibold">
                    {formatCurrency(invoice.outstanding, invoice.currency)}
                  </span>
                </Field>
              </Section>

              {invoice.memo && (
                <Section title="Memo">
                  <div className="sm:col-span-3 text-sm whitespace-pre-wrap">{invoice.memo}</div>
                </Section>
              )}

              <Section title="Audit">
                <Field label="Created">{formatDateTime(invoice.createdAt)}</Field>
                <Field label="Created by">
                  <ActorLink actorId={invoice.createdBy} />
                </Field>
                <Field label="Updated">{formatDateTime(invoice.updatedAt)}</Field>
                <Field label="Updated by">
                  <ActorLink actorId={invoice.updatedBy} />
                </Field>
              </Section>
            </CardContent>
          </Card>

          <h2 className="mb-4 mt-8 text-lg font-semibold">Line items</h2>
          <LineItemsTable
            items={invoice.lineItems ?? []}
            currency={invoice.currency}
            organisationId={organisationId}
          />
        </>
      ) : (
        <p className="text-muted-foreground">Invoice not found.</p>
      )}

      <h2 className="mb-4 mt-8 text-lg font-semibold">Payments</h2>
      <InvoicePaymentsTable invoiceId={invoiceId} organisationId={organisationId} />

      <h2 className="mb-4 mt-8 text-lg font-semibold">Invoice Reminders</h2>
      <InvoiceRemindersTable invoiceId={invoiceId} organisationId={organisationId} />

      <h2 className="mb-4 mt-8 text-lg font-semibold">Credit Notes</h2>
      <InvoiceCreditNotesTable invoiceId={invoiceId} />

      <h2 className="mb-4 mt-8 text-lg font-semibold">Audit log</h2>
      <InvoiceAuditLogTable invoiceId={invoiceId} />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">{children}</dl>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function ActorLink({ actorId }: { actorId: string | null | undefined }) {
  if (!actorId) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/users/${actorId}`}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {actorId.slice(0, 8)}...
      </Badge>
    </Link>
  );
}

function LineItemsTable({
  items,
  currency,
  organisationId,
}: {
  items: LineItem[];
  currency: string;
  organisationId: string | undefined;
}) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Tax rate</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead className="text-right">Unit price</TableHead>
            <TableHead className="text-right">Tax</TableHead>
            <TableHead className="text-right">Subtotal</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-muted-foreground h-24 text-center">
                No line items.
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.description}</TableCell>
                <TableCell>
                  {item.productId ? (
                    organisationId ? (
                      <Link to={`/products/${item.productId}?organisationId=${organisationId}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {item.productId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <Badge variant="secondary" className="font-mono text-xs">
                        {item.productId.slice(0, 8)}...
                      </Badge>
                    )
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {item.taxRateSnapshot ? (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-mono">{item.taxRateSnapshot.code}</span>
                      <span className="text-muted-foreground">@ {item.taxRateSnapshot.rate}%</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="font-normal">Snapshot</Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          {item.taxRateSnapshot.source === "country" ? "Country default" : "Org rate"}
                          {item.taxRateSnapshot.snapshottedAt && ` · frozen ${new Date(item.taxRateSnapshot.snapshottedAt).toLocaleDateString()}`}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  ) : item.taxRateId ? (
                    <Badge variant="secondary" className="font-mono text-xs">
                      {item.taxRateId.slice(0, 8)}...
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">{formatNumber(item.quantity)}</TableCell>
                <TableCell className="text-right">
                  {formatCurrency(item.unitPrice, currency)}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(item.taxAmount, currency)}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(item.subTotal, currency)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
